import {
	Close,
	Delete,
	Done,
	KeyboardDoubleArrowLeft,
	PushPin,
	PushPinOutlined,
	Add,
	EditOutlined,
} from '@mui/icons-material'
import React, { FC, Fragment, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'
import {
	Button,
	IconButton,
	Input,
	OptionsMenuButton,
	Tooltip,
} from '../../../../../components/ui'
import cn from '../../../../../lib/utils/cn'
import { useGetFileChats } from '../../../../../hooks/api/documentHooks'
import { ChatsItem } from '../../../../../lib/api/types/document.interfaces'
import {
	useCreateChat,
	useDeleteChat,
	useUpdateChat,
} from '../../../../../hooks/api/chatHooks'
import {
	ConfirmationModal,
	InputModal,
} from '../../../../../components/modals/common'

interface ChatsMenuProps {
	isOpen: boolean
	onClose: () => void
	onSelect: (chat: ChatsItem | null) => void
	onSave?: () => void
	selectedChat: ChatsItem | null
	currentFileId?: string | null
}

const ChatsMenu: FC<ChatsMenuProps> = ({
	isOpen,
	onClose,
	onSelect,
	onSave,
	selectedChat,
	currentFileId = '',
}) => {
	const isMedium = useMediaQuery({ maxWidth: 1024 })
	const [newChatBlank, setNewChatBlank] = useState(false)
	const [deletingChat, setDeletingChat] = useState<null | ChatsItem>(null)
	const [editingChat, setEditingChat] = useState<null | ChatsItem>(null)

	const { control, watch, reset } = useForm({
		defaultValues: {
			name: '',
		},
	})
	const { name } = watch()

	const { mutateAsync: createNewChat, isPending: isCreating } = useCreateChat()
	const { mutateAsync: updateChat, isPending: isChanging } = useUpdateChat()
	const { mutateAsync: deleteChat, isPending: isDeleting } = useDeleteChat()

	const { data: chatsData, isLoading } = useGetFileChats({
		id: currentFileId ?? '',
	})

	useEffect(() => {
		if (!selectedChat?.id && chatsData?.data?.length) {
			const tempChat = chatsData?.data?.find((chat) => chat?.isTemporary)
			!!tempChat && onSelect(tempChat)
		}
	}, [chatsData, currentFileId])

	const handleCreateNewChat = async () => {
		if (!currentFileId || isCreating) return
		await createNewChat(
			{ fileId: currentFileId, title: name},
			{
				onSuccess: ({ data }) => {
					setNewChatBlank(false)
					reset({ name: '' })
					if (data?.data?.id) {
						onSelect(data?.data)
					}
				},
			}
		)
	}

	if (!isOpen) {
		return null
	}

	return (
		<Fragment>
			<motion.div
				initial={{ x: '-100%' }}
				animate={{ x: isOpen ? 0 : '-100%' }}
				exit={{ x: '-100%' }}
				transition={{ duration: isMedium ? 0 : 0.1, ease: 'easeInOut' }}
				className={cn(
					'inset-y-0 left-0 z-30 flex items-start justify-start',
					isMedium ? 'absolute inset-x-0 bg-black/20' : 'fixed'
				)}
				onClick={onClose}
			>
				<div
					className="w-40 h-full bg-white dark:bg-background-dark py-2.5 border-r border-stroke-dark dark:border-stroke-dark-dark"
					onClick={(e) => {
						e?.stopPropagation()
					}}
				>
					<div className="w-full flex items-center px-2.5 justify-between box-border mb-5">
						<Button
							variant="secondary"
							onClick={() => {
								if (selectedChat?.id && selectedChat?.isTemporary) {
									onSave?.()
									return
								}
								setNewChatBlank(true)
							}}
							leftIcon={<Add className="w-5" />}
							label="New chat"
							className="w-full justify-center"
							disabled={newChatBlank}
						/>
					</div>
					<div className="relative w-full h-[calc(100vh-70px)] flex-1 box-border px-2.5 py-0.5 flex flex-col pb-10 overflow-hidden">
						{newChatBlank && (
							<div className="flex items-center gap-1 box-border mb-2.5">
								<Controller
									control={control}
									name="name"
									render={({ field: { onChange, value } }) => (
										<Input
											wrapperClassName="w-full flex-1 md:w-[120px] h-9"
											inputClassName="pl-2"
											placeholder="New chat"
											value={value}
											onChange={onChange}
											maxLength={25}
											onEnterClick={handleCreateNewChat}
										/>
									)}
								/>
								<IconButton
									variant="secondary"
									icon={<Close className="dark:text-text-main-dark" />}
									onClick={() => setNewChatBlank(false)}
									className="w-6 h-6"
								/>
								<IconButton
									icon={<Done />}
									onClick={handleCreateNewChat}
									loading={isCreating}
									className="w-6 h-6"
								/>
							</div>
						)}
						<div className="flex flex-col gap-2.5">
							{chatsData?.data?.length
								? chatsData?.data
									?.sort((a, b) => Number(b.isPinned) - Number(a.isPinned))
									?.map((chat, index) => {
										if (chat?.isTemporary) {
											return null
										}

										const isSelected = selectedChat?.id === chat?.id

										return (
											<div
												key={index}
												onClick={(e) => {
													e?.stopPropagation()
													onSelect(isSelected ? null : chat)
													if (isMedium) onClose()
												}}
												className={cn(
													'flex items-center h-10 justify-between w-full rounded-lg pl-3 pr-2 gap-2 text-sm text-text-main box-border cursor-pointer duration-default',
													isSelected
														? 'text-white bg-gradient-to-b from-background-gradient-start to-background-gradient-end ring-none hover:bg-gradient-to-b hover:from-accent-hover hover:to-accent-hover'
														: 'bg-background-light hover:bg-background-light-hover'
												)}
											>
												<Tooltip
													label={
														chat?.title ||
														`Unnamed chat ${chat?.id?.slice(
															chat?.id?.length - 4
														)}`
													}
													className={cn(
														chat?.title?.length < 12 && 'opacity-0'
													)}
													offset={1}
												>
													<div className="flex items-center gap-2 justify-start ellipsis w-full">
														{chat?.isPinned && (
															<PushPin className="w-4 h-4" />
														)}
														<p className="w-auto text-start text-nowrap ellipsis">
															{chat?.title ||
																`Unnamed chat ${chat?.id?.slice(
																	chat?.id?.length - 4
																)}`}
														</p>
													</div>
												</Tooltip>
												<OptionsMenuButton
													buttonClassName={
														isSelected ? 'hover:bg-accent-hover' : ''
													}
													moreIconClassName={isSelected ? 'fill-white' : ''}
													items={[
														{
															icon: chat?.isPinned ? (
																<PushPin className="text-text-secondary" />
															) : (
																<PushPinOutlined />
															),
															label: chat?.isPinned ? 'Pinned' : 'Pin',
															onClick: async () => {
																await updateChat({
																	data: {
																		title: chat?.title,
																		isPinned: !chat?.isPinned,
																		isTemporary: false,
																	},
																	id: chat?.id,
																})
															},
														},
														{
															icon: <EditOutlined />,
															label: 'Rename',
															onClick: () =>
																setEditingChat({
																	...chat,
																	title:
																		chat?.title ||
																		`chat ${chat?.id?.slice(
																			chat?.id?.length - 4
																		)}`,
																}),
														},
														{
															icon: <Delete />,
															label: 'Delete',
															onClick: () => setDeletingChat(chat),
														},
													]}
												/>
											</div>
										)
									})
								: !newChatBlank && (
									<span className="text-xs text-stroke-dark text-center">
										{isLoading ? 'Loading...' : 'No chats'}
									</span>
								)}
						</div>
						<IconButton
							variant="transparent"
							icon={
								<KeyboardDoubleArrowLeft className="dark:text-text-main-dark" />
							}
							onClick={onClose}
							className="absolute bottom-2.5 right-2.5"
						/>
					</div>
				</div>
			</motion.div>
			<InputModal
				isOpen={!!editingChat}
				onClose={() => setEditingChat(null)}
				title="Rename chat"
				icon={<EditOutlined className="dark:text-text-main-dark text-text" />}
				onAction={async (newName: string) => {
					if (editingChat?.id) {
						await updateChat(
							{
								data: {
									title: newName,
									isPinned: editingChat?.isPinned,
									isTemporary: editingChat?.isTemporary,
								},
								id: editingChat?.id,
							},
							{
								onSuccess: () => {
									setEditingChat(null)
								},
							}
						)
					}
				}}
				defaultValue={editingChat?.title || ''}
				loading={isChanging}
			/>
			<ConfirmationModal
				isOpen={!!deletingChat}
				onClose={() => setDeletingChat(null)}
				onAction={async () => {
					if (deletingChat?.id) {
						await deleteChat(
							{
								id: deletingChat?.id,
							},
							{
								onSuccess: () => {
									if (deletingChat?.id === selectedChat?.id) {
										onSelect(null)
									}
								},
							}
						)
					}
				}}
				icon={<Delete />}
				title="Delete Chat"
				description={
					<p>
						Are you sure you want to delete{' '}
						<span>&quot;{deletingChat?.title ?? 'Chat'}&quot;</span>?
					</p>
				}
				loading={isDeleting}
			/>
		</Fragment>
	)
}

export default ChatsMenu
